'use client';

import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useLocale } from '@hooks/useLocale';
import { useTranslations } from 'next-intl';

import { getAlgoliaResults } from '@algolia/autocomplete-js';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import type { LiteClient } from 'algoliasearch/lite';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { createRedirectUrlPlugin } from '@algolia/autocomplete-plugin-redirect-url';
import { ProductItem } from '@components/algolia/ProductItem';
import useBreakpoint from '@hooks/useBreakpoint';
import { isMobile } from 'react-device-detect';
import { getIndex } from '@utils/algolia/analytics';
import { useAlgoliaSearchKey } from '@context/AlgoliaSearchKeyContext';
import { useStorePc } from '@hooks/useStorePc';
import Skeleton from 'react-loading-skeleton';
import styles from './SearchBar.module.scss';

const NewAutocomplete = dynamic(() => import('_components/AutoComplete'), {
  loading: () => <Skeleton className={styles.searchBarSkeleton} />,
  ssr: false,
});

type SearchRequest = {
  indexName: string;
  params: {
    analytics: boolean;
    clickAnalytics: boolean;
    highlightPreTag: string;
    highlightPostTag: string;
    hitsPerPage: number;
    userToken: string;
    query: string;
    ruleContexts?: string[];
  };
};

type AutocompleteInitialState = {
  products: unknown;
  querySuggestions: unknown;
  productsIndex: string;
  querySuggestionsIndex: string;
};

// Dedupe concurrent fetches across multiple SearchBar instances (mobile + desktop).
const initialStatePromiseByLocale = new Map<
  string,
  Promise<AutocompleteInitialState | null>
>();

function fetchAutocompleteInitialState(
  locale: string
): Promise<AutocompleteInitialState | null> {
  const existing = initialStatePromiseByLocale.get(locale);
  if (existing) return existing;

  const promise = fetch(
    `/api/algolia/autocomplete-initial-state?locale=${encodeURIComponent(
      locale
    )}`
  )
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const parsed =
        data && !data.error ? (data as AutocompleteInitialState) : null;
      if (!parsed) initialStatePromiseByLocale.delete(locale);
      return parsed;
    })
    .catch((err) => {
      console.error('Failed to load Algolia autocomplete initial state', err);
      initialStatePromiseByLocale.delete(locale);
      return null;
    });

  initialStatePromiseByLocale.set(locale, promise);
  return promise;
}

const SearchBar = ({
  newSearchBarPlaceholder = false,
}: {
  newSearchBarPlaceholder?: boolean;
}): ReactElement => {
  const locale = useLocale();
  const t = useTranslations('common');
  const algoliaKey = useAlgoliaSearchKey();

  // Shared, cached initial-state response so opening the autocomplete doesn't
  // hit Algolia on every focus. Refreshed by the API route's TTL.
  const initialStateRef = useRef<AutocompleteInitialState | null>(null);

  // Cache redirect lookups per session so submit can resolve a redirect without
  // hitting Algolia. Seeded from the products search the autocomplete already
  // runs while typing (see searchClient.search below).
  // `string` = redirect URL, `null` = confirmed no redirect.
  const redirectCacheRef = useRef<Map<string, string | null>>(new Map());

  useEffect(() => {
    let cancelled = false;
    fetchAutocompleteInitialState(locale).then((data) => {
      if (!cancelled && data) {
        initialStateRef.current = data;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const searchClient = useMemo<any>(() => {
    if (!algoliaKey?.appId || !algoliaKey?.searchKey) return null;
    const client: LiteClient = algoliasearch(
      algoliaKey.appId,
      algoliaKey.searchKey
    );
    return {
      ...client,
      search(requests: SearchRequest[]): Promise<any> {
        // Serve fully-empty-query batches from the autocomplete initial-state cache.
        const initialState = initialStateRef.current;
        if (initialState) {
          const cachedResults = requests.map((r) => {
            const isEmpty = !r.params?.query || r.params.query.length === 0;
            if (!isEmpty) return undefined;
            if (r.indexName === initialState.productsIndex)
              return initialState.products;
            if (r.indexName === initialState.querySuggestionsIndex)
              return initialState.querySuggestions;
            return undefined;
          });
          if (cachedResults.every((r) => r !== undefined)) {
            return Promise.resolve({ results: cachedResults });
          }
        }

        const newRequests = requests.map((request: SearchRequest) => {
          // Empty query here means it's the initial "open/blank" state of autocomplete.
          // We want to exclude these from analytics
          if (!request.params?.query || request.params.query.length === 0) {
            request.params = {
              ...request.params,
              analytics: false,
            };
          }
          return request;
        });
        return client.search(newRequests).then((response: any) => {
          // Seed the redirect cache from the products search the autocomplete
          // already runs while typing, so submit can resolve a redirect from
          // cache instead of issuing its own Algolia query. Redirects only
          // appear on the products index (the `context_autocomplete` rule
          // context); we record misses as `null` too, since the vast majority
          // of queries have no redirect.
          const results = response?.results ?? [];
          requests.forEach((request, i) => {
            const query = request.params?.query?.trim().toLowerCase();
            if (!query) return;
            if (!request.params?.ruleContexts?.includes('context_autocomplete'))
              return;
            const redirectUrl: string | undefined =
              results[i]?.renderingContent?.redirect?.url;
            redirectCacheRef.current.set(query, redirectUrl ?? null);
          });
          return response;
        });
      },
    };
  }, [algoliaKey?.appId, algoliaKey?.searchKey]);

  const indexName = getIndex(locale);
  const querySuggestionIndex = getIndex(locale, '_query_suggestions');
  const breakPoint = useBreakpoint(720);
  const isStorePc = useStorePc();
  const analyticsTags = [
    'mec',
    'autocomplete',
    isMobile ? 'mobile' : 'desktop',
    isStorePc ? 'internal' : 'external',
  ];

  const newSearchPlaceholder = t('search.placeholder.new');

  const searchPlaceholder = breakPoint
    ? t('search.placeholder.mobile')
    : t('search.placeholder._self');

  if (!searchClient || !algoliaKey) {
    return <Skeleton className={styles.searchBarSkeleton} />;
  }

  const { userToken } = algoliaKey;

  const redirectUrlPlugin = createRedirectUrlPlugin({
    // The plugin's default onRedirect navigates using redirect data held in
    // state.context, which can be stale when the user types past a previously
    // resolved query without a new search firing (e.g. fast typing through the
    // debounce). Submit navigation is owned by handleSubmit, which keys off the
    // current query, so we override onRedirect with a no-op to suppress the
    // plugin's own navigation. The redirect cache is seeded centrally from the
    // products search in searchClient.search.
    onRedirect: () => {},
    templates: {
      item({ item, state }) {
        const redirectUrl = item?.urls[0] || '';
        return (
          <a className="aa-ItemLink" href={redirectUrl}>
            {state.query}
          </a>
        );
      },
      header() {
        return (
          <div className="aa-SourceHeader">
            <span className="aa-SourceHeaderTitle">
              {t('search.pages.title')}
            </span>
          </div>
        );
      },
    },
  });

  const querySuggestionsPlugin = createQuerySuggestionsPlugin({
    searchClient,
    indexName: querySuggestionIndex,
    transformSource({ source }) {
      return {
        ...source,
        getItemUrl({ item }) {
          return `/${locale}/search?q=${item.query}`;
        },
        templates: {
          header() {
            return (
              <div
                className="aa-SourceHeader"
                data-testid="autocomplete-suggestions"
              >
                <span className="aa-SourceHeaderTitle">
                  {t('search.suggestions.title')}
                </span>
              </div>
            );
          },

          item(params) {
            const { item } = params;

            return (
              <a
                className="aa-ItemLink"
                href={`/${locale}/search?q=${item.query}`}
              >
                {source.templates.item(params)}
              </a>
            );
          },
        },
      };
    },
  });

  function debouncePromise<T extends (...args: any[]) => any>(
    fn: T,
    time: number
  ): (...args: Parameters<T>) => Promise<ReturnType<T>> {
    let timerId: ReturnType<typeof setTimeout> | undefined = undefined;

    return function debounced(...args: Parameters<T>): Promise<ReturnType<T>> {
      if (timerId !== undefined) {
        clearTimeout(timerId);
      }

      return new Promise((resolve) => {
        timerId = setTimeout(() => resolve(fn(...args)), time);
      });
    };
  }

  const debounced = debouncePromise((items) => Promise.resolve(items), 500);

  const handleSubmit = async ({ state }: { state: { query: string } }) => {
    const query = state.query?.trim();
    const fallback = query
      ? `/${locale}/search?q=${encodeURIComponent(query)}`
      : `/${locale}/search`;

    if (!query) {
      window.location.href = fallback;
      return;
    }

    const cacheKey = query.toLowerCase();
    const cached = redirectCacheRef.current.get(cacheKey);
    if (cached !== undefined) {
      window.location.href = cached || fallback;
      return;
    }

    // Cache miss: the autocomplete search for this exact query hasn't resolved
    // yet (e.g. submit before the typing debounce fired). Fall back to a one-off
    // redirect lookup so redirects still work; this is now rare since the
    // products search seeds the cache for every query the user pauses on.
    try {
      const response: any = await searchClient.search([
        {
          indexName,
          params: {
            query,
            ruleContexts: ['context_autocomplete'],
            hitsPerPage: 0,
            analytics: false,
            clickAnalytics: false,
          },
        },
      ]);
      const redirectUrl: string | undefined =
        response?.results?.[0]?.renderingContent?.redirect?.url;
      redirectCacheRef.current.set(cacheKey, redirectUrl ?? null);
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
    } catch {
      // fall through to default search results page
    }

    window.location.href = fallback;
  };

  return (
    <NewAutocomplete
      openOnFocus={true}
      placeholder={
        newSearchBarPlaceholder ? newSearchPlaceholder : searchPlaceholder
      }
      insights={true}
      plugins={[querySuggestionsPlugin, redirectUrlPlugin]}
      onSubmit={handleSubmit}
      getSources={({ query }: { query: string }) =>
        debounced([
          {
            sourceId: 'products',
            getItemUrl({ item }: any) {
              return item.url;
            },
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName,
                    params: {
                      query,
                      analyticsTags,
                      ruleContexts: ['context_autocomplete'],
                      userToken,
                    },
                  },
                ],
              });
            },
            templates: {
              item({ item, components }: any) {
                return (
                  <ProductItem
                    hit={item}
                    components={components}
                    locale={locale}
                  />
                );
              },
              noResults() {
                return <p>{t('search.results.none')}</p>;
              },
              header() {
                return (
                  <>
                    <span className="aa-SourceHeaderTitle">
                      {t('search.products.title')}
                    </span>
                  </>
                );
              },
            },
          },
        ])
      }
    />
  );
};

export default SearchBar;
